/**
 * Profiles page for managing viewing profiles
 * Allows users to create and manage multiple profiles (kids, adults, etc.)
 */

import type { ReactElement } from "react";
import { Plus, User, Users } from "lucide-react";
import { Card } from "../../shared/components/ui/card";

interface ViewingProfile {
  id: string;
  name: string;
  avatar: string;
  isKids: boolean;
}

// Mock data - will be replaced with API call
const MOCK_PROFILES: ViewingProfile[] = [{ id: "1", name: "Primary", avatar: "👤", isKids: false }];

export function Profiles(): ReactElement {
  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <Users className="h-5 w-5 text-lumi-accent" />
          <h1 className="text-2xl font-bold text-white">Profiles</h1>
        </div>
        <p className="text-xs text-neutral-400 pb-6">Manage profiles for your household.</p>
      </div>

      {/* Current Profiles Grid */}
      <div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {MOCK_PROFILES.map((profile) => (
            <Card key={profile.id} className="bg-transparent border-none cursor-pointer group">
              <div className="flex flex-col items-center justify-center gap-3 h-full min-h-[140px]">
                <div className="w-20 h-20 bg-neutral-800 rounded-full flex items-center justify-center text-4xl group-hover:bg-neutral-700 transition-colors">
                  {profile.avatar}
                </div>
                <div className="text-center">
                  <p className="font-medium text-white">{profile.name}</p>
                  {profile.isKids && <span className="text-xs text-neutral-400">Kids Profile</span>}
                </div>
              </div>
            </Card>
          ))}

          {/* Add Profile Card */}
          <Card className="bg-transparent border-neutral-800 border-dashed p-4 hover:border-lumi-accent/50 hover:bg-neutral-900/50 transition-all cursor-pointer group">
            <div className="flex flex-col items-center justify-center gap-3 h-full min-h-[140px]">
              <div className="w-20 h-20 rounded-full flex items-center justify-center border-dashed group-hover:border-lumi-accent/50 transition-colors">
                <Plus className="h-8 w-8 text-neutral-500 group-hover:text-lumi-accent transition-colors" />
              </div>
              <p className="font-medium text-neutral-400 group-hover:text-white transition-colors">Add Profile</p>
            </div>
          </Card>
        </div>
      </div>

      {/* Profile Features */}
      <Card className="bg-transparent border-none p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Profile Features</h2>
        <div className="space-y-4">
          <div className="flex gap-3">
            <div className="p-2 bg-lumi-accent/10 rounded-lg h-fit">
              <User className="h-4 w-4 text-lumi-accent" />
            </div>
            <div>
              <h3 className="font-medium text-white">Personalized Recommendations</h3>
              <p className="text-sm text-neutral-400">
                Each profile gets content recommendations based on their viewing history and preferences
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="p-2 bg-lumi-accent/10 rounded-lg h-fit">
              <User className="h-4 w-4 text-lumi-accent" />
            </div>
            <div>
              <h3 className="font-medium text-white">Kids Profiles</h3>
              <p className="text-sm text-neutral-400">
                Create kid-safe profiles with age-appropriate content filtering
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="p-2 bg-lumi-accent/10 rounded-lg h-fit">
              <User className="h-4 w-4 text-lumi-accent" />
            </div>
            <div>
              <h3 className="font-medium text-white">Watch History</h3>
              <p className="text-sm text-neutral-400">
                Keep your progress separate - resume watching right where you left off
              </p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
